import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Car, FileText, LogOut, Copy, Lock, Wallet, Calendar, CreditCard, ChevronDown, ChevronRight, Eye, Clock, Award, BarChart3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTH_NAMES_RO = ["Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie", "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];

const getMonthKey = (dateStr) => {
  if (!dateStr) return "necunoscut";
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const getMonthLabel = (key) => {
  if (key === "necunoscut") return "Necunoscut";
  const [year, month] = key.split("-");
  return `${MONTH_NAMES_RO[parseInt(month) - 1]} ${year}`;
};

const getCurrentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const StatusBadge = ({ status }) => {
  const styles = {
    "In verificare": "bg-blue-100 text-blue-700",
    "Acceptat": "bg-amber-100 text-amber-700",
    "In lucru": "bg-purple-100 text-purple-700",
    "Finalizat": "bg-emerald-100 text-emerald-700",
    "Respins": "bg-red-100 text-red-700",
  };
  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || "bg-gray-100 text-gray-700"}`}>{status}</span>;
};

const MonthGroup = ({ monthKey, items, renderItem, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden" data-testid={`month-group-${monthKey}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span className="font-semibold text-gray-800">{getMonthLabel(monthKey)}</span>
          <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">{items.length}</span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="divide-y divide-gray-50">{items.map(renderItem)}</div>}
    </div>
  );
};

const AffiliateDashboard = () => {
  const navigate = useNavigate();
  const { afiliatToken, afiliatData, logoutAfiliat } = useAuth();
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [payments, setPayments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dosare");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ parola_curenta: "", parola_noua: "", confirma: "" });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const isAdmin = afiliatData?.is_admin;

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${afiliatToken}` };
      const [statsRes, casesRes, paymentsRes] = await Promise.all([
        fetch(`${API}/affiliate/dashboard`, { headers }).then(r => r.json()),
        fetch(`${API}/affiliate/cases`, { headers }).then(r => r.json()),
        !isAdmin ? fetch(`${API}/affiliate/payments`, { headers }).then(r => r.json()) : Promise.resolve(null)
      ]);
      setStats(statsRes);
      setCases(Array.isArray(casesRes) ? casesRes : []);
      if (paymentsRes) setPayments(paymentsRes);
    } catch (err) {
      toast.error("Eroare la incarcarea datelor");
    } finally {
      setLoading(false);
    }
  }, [afiliatToken, isAdmin]);

  useEffect(() => {
    if (!afiliatToken) { navigate("/affiliate/login"); return; }
    fetchData();
  }, [afiliatToken, navigate, fetchData]);

  const copyCode = () => {
    if (isAdmin) return;
    navigator.clipboard.writeText(stats?.cod_afiliat || afiliatData?.cod_afiliat || "");
    toast.success("Cod copiat!");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.parola_noua !== passwordForm.confirma) { toast.error("Parolele nu coincid"); return; }
    setPasswordLoading(true);
    try {
      await fetch(`${API}/affiliate/password`, { method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${afiliatToken}` }, body: JSON.stringify({ parola_curenta: passwordForm.parola_curenta, parola_noua: passwordForm.parola_noua }) });
      toast.success("Parola a fost schimbata!");
      setShowPasswordModal(false);
      setPasswordForm({ parola_curenta: "", parola_noua: "", confirma: "" });
    } catch (error) {
      toast.error("Eroare");
    } finally {
      setPasswordLoading(false);
    }
  };

  const currentCode = stats?.cod_afiliat || afiliatData?.cod_afiliat || "";
  const currentMonth = getCurrentMonthKey();

  // Group cases by month
  const currentMonthCases = cases.filter(c => getMonthKey(c.created_at || c.data) === currentMonth);
  const historyCases = cases.filter(c => getMonthKey(c.created_at || c.data) !== currentMonth);
  const historyMonths = {};
  historyCases.forEach(c => {
    const key = getMonthKey(c.created_at || c.data);
    if (!historyMonths[key]) historyMonths[key] = [];
    historyMonths[key].push(c);
  });
  const sortedHistoryMonths = Object.keys(historyMonths).sort().reverse();

  // Group payments by month
  const paymentsList = payments?.plati || [];
  const currentMonthPayments = paymentsList.filter(p => getMonthKey(p.data) === currentMonth);
  const historyPayments = paymentsList.filter(p => getMonthKey(p.data) !== currentMonth);
  const historyPayMonths = {};
  historyPayments.forEach(p => {
    const key = getMonthKey(p.data);
    if (!historyPayMonths[key]) historyPayMonths[key] = [];
    historyPayMonths[key].push(p);
  });
  const sortedHistoryPayMonths = Object.keys(historyPayMonths).sort().reverse();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const renderCaseRow = (c) => (
    <div key={c.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors" data-testid={`case-row-${c.id}`}>
      <div className="flex items-center gap-4 min-w-0 flex-1">
        <span className="font-mono text-sm font-semibold text-emerald-600 shrink-0">{c.numar_dosar}</span>
        <span className="text-sm text-gray-600 truncate">{c.client}</span>
        <span className="text-xs text-gray-400 shrink-0">{c.data}</span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <StatusBadge status={c.status} />
        {c.comision > 0 && <span className="text-sm font-semibold text-emerald-600">{c.comision} lei</span>}
        <Link to={`/affiliate/cases/${c.id}`} className="p-1.5 text-gray-400 hover:text-emerald-600 rounded-lg hover:bg-emerald-50" data-testid={`view-case-${c.id}`}>
          <Eye className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );

  const renderPaymentRow = (p) => (
    <div key={p.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50" data-testid={`payment-row-${p.id}`}>
      <div className="flex items-center gap-4">
        <CreditCard className="w-4 h-4 text-blue-500" />
        <span className="text-sm text-gray-700">{p.data?.slice(0, 10)}</span>
        {p.nota && <span className="text-xs text-gray-500">{p.nota}</span>}
      </div>
      <span className="text-sm font-bold text-emerald-600">+{p.suma} lei</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="affiliate-dashboard">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center"><Car className="w-5 h-5 text-white" /></div>
              <span className="font-bold text-gray-900">{isAdmin ? "Admin Portal" : "Portal Afiliati"}</span>
              {isAdmin && <span className="ml-2 px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded">ADMIN</span>}
            </div>
            <div className="flex items-center gap-4">
              <span className="text-gray-600 hidden sm:block">Buna, <strong>{afiliatData?.prenume}</strong></span>
              {!isAdmin && <button onClick={() => setShowPasswordModal(true)} className="p-2 text-gray-500 hover:text-emerald-600" title="Schimba parola"><Lock className="w-5 h-5" /></button>}
              <button onClick={() => { logoutAfiliat(); navigate("/"); }} className="p-2 text-gray-500 hover:text-gray-700" data-testid="affiliate-logout"><LogOut className="w-5 h-5" /></button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Cod recomandare */}
        {!isAdmin && (
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl p-6 mb-8 text-white">
            <h2 className="text-lg font-bold mb-2">Codul tau de recomandare</h2>
            <p className="text-emerald-100 text-sm mb-4">Distribuie acest cod pentru a primi comision de 200 lei per dosar finalizat</p>
            <div className="bg-white/20 rounded-xl p-4 flex items-center justify-between max-w-sm">
              <span className="font-mono text-2xl font-bold" data-testid="affiliate-code">{currentCode}</span>
              <button onClick={copyCode} className="p-2 hover:bg-white/20 rounded-lg" data-testid="copy-code-btn"><Copy className="w-5 h-5" /></button>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-5 rounded-2xl border border-gray-100" data-testid="stat-total">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center"><FileText className="w-5 h-5 text-blue-600" /></div>
              <div><p className="text-xs text-gray-500">Total dosare</p><p className="text-xl font-bold text-gray-900">{stats?.total_dosare || 0}</p></div>
            </div>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-gray-100" data-testid="stat-in-lucru">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center"><Clock className="w-5 h-5 text-purple-600" /></div>
              <div><p className="text-xs text-gray-500">In lucru</p><p className="text-xl font-bold text-gray-900">{stats?.dosare_in_lucru || 0}</p></div>
            </div>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-gray-100" data-testid="stat-finalizate">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center"><Award className="w-5 h-5 text-emerald-600" /></div>
              <div><p className="text-xs text-gray-500">Finalizate</p><p className="text-xl font-bold text-gray-900">{stats?.dosare_finalizate || 0}</p></div>
            </div>
          </div>
          {!isAdmin && (
            <div className="bg-white p-5 rounded-2xl border border-gray-100" data-testid="stat-comision">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center"><Wallet className="w-5 h-5 text-amber-600" /></div>
                <div><p className="text-xs text-gray-500">De incasat</p><p className="text-xl font-bold text-amber-600">{stats?.comision_de_plata || 0} lei</p></div>
              </div>
            </div>
          )}
        </div>

        {/* Tabs: Dosare / Plati */}
        {!isAdmin && (
          <div className="flex gap-2 mb-6" data-testid="affiliate-tabs">
            <button onClick={() => setActiveTab("dosare")} className={`px-6 py-2 rounded-full font-medium transition-colors ${activeTab === "dosare" ? "bg-emerald-500 text-white" : "bg-white text-gray-500 hover:text-gray-700 border border-gray-200"}`} data-testid="tab-dosare">
              Dosare ({cases.length})
            </button>
            <button onClick={() => setActiveTab("plati")} className={`px-6 py-2 rounded-full font-medium transition-colors ${activeTab === "plati" ? "bg-emerald-500 text-white" : "bg-white text-gray-500 hover:text-gray-700 border border-gray-200"}`} data-testid="tab-plati">
              Plati ({paymentsList.length})
            </button>
          </div>
        )}

        {/* ===== DOSARE TAB ===== */}
        {(activeTab === "dosare" || isAdmin) && (
          <div className="space-y-6">
            {/* Luna curenta */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="dosare-luna-curenta">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-500" />
                <h3 className="font-bold text-gray-900">Luna curenta - {getMonthLabel(currentMonth)}</h3>
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">{currentMonthCases.length}</span>
              </div>
              {currentMonthCases.length === 0 ? (
                <div className="px-5 py-8 text-center">
                  <FileText className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">Niciun dosar in luna curenta</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">{currentMonthCases.map(renderCaseRow)}</div>
              )}
            </div>

            {/* Istoric */}
            {sortedHistoryMonths.length > 0 && (
              <div data-testid="dosare-istoric">
                <h3 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-gray-400" /> Istoric dosare
                </h3>
                <div className="space-y-3">
                  {sortedHistoryMonths.map(mk => (
                    <MonthGroup key={mk} monthKey={mk} items={historyMonths[mk]} renderItem={renderCaseRow} defaultOpen={false} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== PLATI TAB ===== */}
        {activeTab === "plati" && !isAdmin && (
          <div className="space-y-6">
            {/* Sumar financiar */}
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                <div className="flex items-center gap-2 mb-2"><BarChart3 className="w-4 h-4 text-emerald-600" /><span className="text-sm text-emerald-700 font-medium">Total castigat</span></div>
                <p className="text-2xl font-bold text-emerald-600">{payments?.comision_total || 0} lei</p>
                <p className="text-xs text-emerald-600 mt-1">{payments?.dosare_finalizate || 0} dosare x 200 lei</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-center gap-2 mb-2"><CreditCard className="w-4 h-4 text-blue-600" /><span className="text-sm text-blue-700 font-medium">Platit</span></div>
                <p className="text-2xl font-bold text-blue-600">{payments?.comision_platit || 0} lei</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                <div className="flex items-center gap-2 mb-2"><Wallet className="w-4 h-4 text-amber-600" /><span className="text-sm text-amber-700 font-medium">De incasat</span></div>
                <p className="text-2xl font-bold text-amber-600">{payments?.comision_de_plata || 0} lei</p>
              </div>
            </div>

            {/* Luna curenta plati */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="plati-luna-curenta">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-500" />
                <h3 className="font-bold text-gray-900">Plati luna curenta - {getMonthLabel(currentMonth)}</h3>
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">{currentMonthPayments.length}</span>
              </div>
              {currentMonthPayments.length === 0 ? (
                <div className="px-5 py-8 text-center">
                  <CreditCard className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">Nicio plata in luna curenta</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">{currentMonthPayments.map(renderPaymentRow)}</div>
              )}
            </div>

            {/* Istoric plati */}
            {sortedHistoryPayMonths.length > 0 && (
              <div data-testid="plati-istoric">
                <h3 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-gray-400" /> Istoric plati
                </h3>
                <div className="space-y-3">
                  {sortedHistoryPayMonths.map(mk => (
                    <MonthGroup key={mk} monthKey={mk} items={historyPayMonths[mk]} renderItem={renderPaymentRow} defaultOpen={false} />
                  ))}
                </div>
              </div>
            )}

            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm text-gray-600">
                <strong className="text-gray-900">Cum functioneaza:</strong> Comisioanele pentru dosarele finalizate intr-o luna
                se platesc pe data de <strong>10 a lunii urmatoare</strong>. Vei primi plata prin transfer bancar.
              </p>
            </div>
          </div>
        )}
      </main>

      {/* Password Modal */}
      {showPasswordModal && !isAdmin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2"><Lock className="w-5 h-5 text-emerald-500" />Schimba Parola</h3>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Parola curenta</label><input type="password" value={passwordForm.parola_curenta} onChange={(e) => setPasswordForm({ ...passwordForm, parola_curenta: e.target.value })} required className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Parola noua</label><input type="password" value={passwordForm.parola_noua} onChange={(e) => setPasswordForm({ ...passwordForm, parola_noua: e.target.value })} required className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Confirma parola</label><input type="password" value={passwordForm.confirma} onChange={(e) => setPasswordForm({ ...passwordForm, confirma: e.target.value })} required className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white" /></div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowPasswordModal(false)} className="flex-1 h-12 border border-gray-200 text-gray-600 rounded-full hover:bg-gray-50">Anuleaza</button>
                <button type="submit" disabled={passwordLoading} className="flex-1 h-12 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 disabled:bg-emerald-300">{passwordLoading ? "Se salveaza..." : "Salveaza"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliateDashboard;
